/* eslint-disable camelcase */
import styled from 'styled-components';
import React, { useContext, useState, useCallback } from 'react';
import {
  AuthContext,
  ContextualChatContext,
  LeftSidePanelPageType,
} from 'contexts';
import { Icon, Title, Avatar, Text, Button, theme, Empty } from 'components';
import type { UserResponse } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import { useDebounce, useStartChat } from 'hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { Loading } from '@app/react-common';
import { Color } from '@bb/elements-web-rebranding';

const USERS_PER_PAGE = 100;

const NewChatPanel = () => {
  const { setLeftSidePanelPage } = useContext(ContextualChatContext);
  const { auth } = useContext(AuthContext);
  const { client } = useChatContext();
  const { startChat } = useStartChat();
  const [selectedUsers, setSelectedUsers] = useState<UserResponse[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isGroupSelection, setIsGroupSelection] = useState(false);
  const debouncedSearchQuery = useDebounce(inputValue, 300);

  const parentRef = React.useRef(null);

  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['userSearch', auth?.streamChatId, debouncedSearchQuery],
    enabled: !!auth?.streamChatId,
    initialData: { pages: [], pageParams: [] },
    queryFn: async ({ pageParam = 0 }) => {
      const filters: Record<string, any> = {
        id: { $ne: auth?.streamChatId || '' },
        role: { $ne: 'anonymous' },
      };
      if (debouncedSearchQuery.trim()) {
        filters.name = { $autocomplete: debouncedSearchQuery.trim() };
      }
      const response = await client.queryUsers(
        filters,
        { name: 1 },
        {
          limit: USERS_PER_PAGE,
          offset: pageParam,
        },
      );
      return response.users;
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (acc, page) => acc + page?.length,
        0,
      );
      return lastPage?.length === USERS_PER_PAGE ? totalFetched : undefined;
    },
  });

  const users = data?.pages?.flat() ?? [];

  const rowVirtualizer = useVirtualizer({
    count: users?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 70,
    overscan: 5,
  });

  const toggleUserSelection = useCallback((userToToggle: UserResponse) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.bbUserId === userToToggle.bbUserId);
      return isSelected
        ? prev.filter(u => u.bbUserId !== userToToggle.bbUserId)
        : [...prev, userToToggle];
    });
  }, []);

  const handleScroll = useCallback(() => {
    const el = parentRef.current;
    if (!el || isFetchingNextPage || !hasNextPage) return;

    const threshold = 100;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const clearSearch = useCallback(() => {
    setInputValue('');
  }, []);

  const handleStartGroupChat = useCallback(() => {
    if (!client.userID || selectedUsers.length === 0) return;
    startChat({
      userId: client.userID,
      selectedUsers,
    });
  }, [client.userID, selectedUsers, startChat]);

  const handleGroupChatWarning = () => {
    if (!client.userID || selectedUsers?.length === 0) return;

    handleStartGroupChat();
  };

  const handleStartSingleChat = useCallback(
    (singleUser: UserResponse) => {
      if (!client.userID || !singleUser) return;
      startChat({
        userId: client.userID,
        selectedUsers: [singleUser],
      });
    },
    [client.userID, startChat],
  );

  const handleUserSelection = useCallback(
    (userItem: UserResponse) => {
      if (isGroupSelection) {
        toggleUserSelection(userItem);
        return;
      }
      handleStartSingleChat(userItem);
    },
    [handleStartSingleChat, isGroupSelection, toggleUserSelection],
  );

  const renderUserItem = useCallback(
    ({ item, index }: { item: UserResponse; index: number }) => {
      const isSelected = selectedUsers.some(u => u.bbUserId === item.bbUserId);
      const isLastItem = index === users.length - 1;

      const subText = item?.org?.name || item?.mlsTypes?.at(0) || '';

      return (
        <UserItemContainer
          onClick={() => handleUserSelection(item)}
          isSelected={isSelected}
          hasBottomBorder={!isLastItem}
          key={`${item.bbUserId}-${index}`}
        >
          <Avatar
            user={{
              ...item,
              firstName: item.name.split(' ').at(0) || '',
              lastName: item.name.split(' ').at(1) || '',
            }}
          />
          <UserInfo>
            <UserNameText>{item.name}</UserNameText>
            <UserSubText>{subText}</UserSubText>
          </UserInfo>
          {isGroupSelection && (
            <SelectionIndicator>
              {isSelected ? (
                <Icon type="antdCheckCircleFilled" width={20} color="primary" />
              ) : (
                <Icon type="circleOutline" width={20} color="textSecondary" />
              )}
            </SelectionIndicator>
          )}
        </UserItemContainer>
      );
    },
    [selectedUsers, users.length, isGroupSelection, handleUserSelection],
  );

  return (
    <StyledContainer>
      <StyledCard>
        <StyledPrimaryHeader>
          <StyledAction
            type="antdArrowLeftOutlined"
            width={18}
            color="primary"
            onClick={() =>
              setLeftSidePanelPage(LeftSidePanelPageType.ChannelList)
            }
          />
          <div>
            <Title level={2} hideMargin>
              {isGroupSelection ? 'New group message' : 'New message'}
            </Title>
            {isGroupSelection && (
              <UserSubText>{selectedUsers?.length} selected</UserSubText>
            )}
          </div>
        </StyledPrimaryHeader>
      </StyledCard>
      <SearchHeader>
        <SearchInputWrapper>
          <Icon type="search" width={20} />
          <SearchInput
            placeholder="Search"
            onChange={e => setInputValue(e.target.value)}
            value={inputValue}
          />
          {inputValue ? (
            <ClearButton onClick={clearSearch}>
              <Icon type="antdCloseCircleFilled" width={18} />
            </ClearButton>
          ) : null}
        </SearchInputWrapper>
      </SearchHeader>
      {!isGroupSelection && users?.length ? (
        <StyledNewGroupMessageButton
          type="link"
          onClick={() => {
            setIsGroupSelection(true);
          }}
        >
          <Icon type="antdUserGroupAddOutlined" color="primary" />
          New group message
        </StyledNewGroupMessageButton>
      ) : undefined}
      {isGroupSelection && (
        <SelectedUsersAvatars>
          {selectedUsers.map((user, idx) => (
            <StyledAvatarButtonContainer
              onClick={() => toggleUserSelection(user)}
              key={`${user.bbUserId}-${idx}`}
            >
              <Icon
                type="antdCloseCircleFilled"
                width={18}
                color="black"
                style={{
                  position: 'relative',
                  left: '18px', // Based on half of Avatar width + 6px for overlap
                  top: '14px',
                  zIndex: 1,
                }}
              />
              <Avatar
                user={{
                  ...user,
                  firstName: user.name?.split(' ').at(0) || '',
                  lastName: user.name?.split(' ').at(1) || '',
                }}
                width={42}
              />
              <Text style={{ fontSize: '12px' }}>
                {user.name.split(' ').at(0)}
              </Text>
            </StyledAvatarButtonContainer>
          ))}
        </SelectedUsersAvatars>
      )}
      {isFetching && !users?.length ? (
        <Loading />
      ) : (
        <>
          {isError && !isFetching ? (
            <RetryContainer>
              <Text style={{ textAlign: 'center' }}>
                Sorry, we were unable to load the users.
              </Text>
              <Button onClick={() => refetch()}>Retry</Button>
            </RetryContainer>
          ) : !users?.length ? (
            <Empty description="No results found" style={{ marginTop: 50 }} />
          ) : (
            <UsersItemsContainer
              onScroll={handleScroll}
              ref={parentRef}
              className="List"
            >
              <VirtualContainer height={rowVirtualizer.getTotalSize()}>
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                  const user = users[virtualRow.index];
                  if (!user) return null;

                  return (
                    <VirtualizedRow
                      key={virtualRow.index}
                      virtualRow={virtualRow}
                    >
                      {renderUserItem({ item: user, index: virtualRow.index })}
                    </VirtualizedRow>
                  );
                })}
              </VirtualContainer>
              {isFetching && <Loading />}
            </UsersItemsContainer>
          )}
        </>
      )}

      {selectedUsers.length > 0 && (
        <StyledCreateChatButtonContainer>
          <StyledCreateChatButton
            type="primary"
            onClick={handleGroupChatWarning}
            size="large"
          >
            {`${selectedUsers.length > 1 ? 'Create group message' : 'Start chat'}`}
          </StyledCreateChatButton>
        </StyledCreateChatButtonContainer>
      )}
    </StyledContainer>
  );
};

const RetryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-direction: column;
  flex: 1;
  margin-top: 15px;
`;

const VirtualContainer = styled.div<{ height: number }>`
  height: ${({ height }) => `${height}px`};
  width: 100%;
  position: relative;
`;

const VirtualizedRow = styled.div<{ virtualRow: VirtualItem }>`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: ${({ virtualRow }) =>
    virtualRow ? `${virtualRow.size}px` : undefined};
  transform: ${({ virtualRow }) =>
    virtualRow ? `translateY(${virtualRow.start}px)` : undefined};
`;
const StyledContainer = styled.div`
  min-width: 385px;
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px #e3e3e3 solid;
`;
const StyledCard = styled.div`
  padding: 25px 20px;
`;
const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 12px;
`;
const StyledAction = styled(Icon)`
  margin-left: 10px;
`;
// #region - Search Bar styles
const SearchHeader = styled.div`
  padding: 8px 16px;
  background-color: #fff;
  border-bottom-width: 1px;
  border-bottom-color: #e9ecef;
`;
const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fff;
  border-radius: 8px;
  padding: 6px 8px;
  border: solid 1px #c5c6d0;
  gap: 8px;
`;
const SearchInput = styled.input`
  flex: 1;
  font-size: 16px;
  color: #333;
  padding: 0;
  border: none;
  background-color: transparent;
  &:focus {
    outline: none;
  }
`;
const ClearButton = styled.button`
  margin-left: 8px;
  display: flex;
`;

// #endregion Search Bar styles
// #region - Selected Avatars styles
const SelectedUsersAvatars = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #c5c6d0 #fff;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c5c6d0;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    z-index: 1;
  }
`;
const StyledAvatarButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  &:hover {
    cursor: pointer;
  }
`;
// #endregion Selected Avatars styles
// #region - User Item styles
const UsersItemsContainer = styled.div`
  overflow-y: auto;
  flex: 1;
  height: 500px;
`;
const UserItemContainer = styled.button<{
  isSelected: boolean;
  hasBottomBorder: boolean;
}>`
  display: flex;
  height: 70px;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 0px 16px;
  background-color: ${p => (p.isSelected ? '#f0f8ff' : '#fff')};
  ${p =>
    p.isSelected &&
    `
    border-left-width: 4px;
    border-left-color: ${theme.getColor('primary')};
    border-left-style: solid;
  `}
`;
const SelectionIndicator = styled.div`
  justify-content: center;
  align-items: center;
`;
const UserInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-bottom: solid 1px #c5c6d0;
  padding: 10px 0px;
  margin-right: 12px;
  margin-left: 12px;
  gap: 4px;
  height: 100%;
`;
const UserNameText = styled(Text)`
  font-size: 14px;
  color: #1a1a1a;
`;
const UserSubText = styled(Text)`
  font-size: 14px;
  color: #6c757d;
`;
// #endregion User Item styles
const StyledCreateChatButtonContainer = styled.div`
  position: relative;
  width: 100%;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 16px;
`;
const StyledCreateChatButton = styled(Button)`
  width: 100%;
  font-size: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
`;
const StyledNewGroupMessageButton = styled(Button)`
  margin-top: 10px;
  margin-bottom: 10px;
  justify-self: left;
  text-align: left;
  color: ${Color.PRIMARY};
`;
export default NewChatPanel;
